import type { LiveChatAgentMessageEventData } from 'api/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect } from 'react';
import { selectApiKey } from 'reducers/config';
import {
  onAgentConnected,
  selectLiveChatIntegration,
} from 'reducers/live-chat';
import {
  addWidgetComponents,
  selectConversationId,
  selectIsLiveChatMode,
} from 'reducers/workflows';
import notificationBellSound from '../../../assets/sounds/notification_bell.mp3';
import { addLiveChatEventMessage, concludeLiveChat } from 'actions/live-chat';
import { getOrCreatePusher } from './useInitPusher';
import { isPusherLiveChatIntegration } from 'api/types';
import { PUSHER_ENCRYPTED_CHANNEL_PREFIX } from '../consts';

const LIVE_CHAT_AGENT_MESSAGE = 'live-chat-agent-message';
const LIVE_CHAT_CONVERSATION_RESOLVED = 'live-chat-conversation-resolved';

export const usePusherLiveChatMessageEventSubscription = () => {
  const dispatch = useAppDispatch();
  const apiKey = useAppSelector(selectApiKey);
  const conversationId = useAppSelector(selectConversationId);
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);

  useEffect(() => {
    if (!isLiveChatMode || !isPusherLiveChatIntegration(liveChatIntegration)) {
      return;
    }

    const pusher = getOrCreatePusher();

    const channelName = `${PUSHER_ENCRYPTED_CHANNEL_PREFIX}solve;live-chat;${apiKey};${conversationId}`;
    const channel = pusher.subscribe(channelName);

    const bindEvents = () => {
      channel.bind(
        LIVE_CHAT_AGENT_MESSAGE,
        ({
          agent,
          articles,
          attachments,
          avatar,
          message,
          type,
        }: LiveChatAgentMessageEventData) => {
          const notificationBell = new Audio(notificationBellSound);

          switch (type) {
            case 'event':
              dispatch(
                addLiveChatEventMessage({
                  message,
                }),
              );

              break;
            case 'message':
              dispatch(
                onAgentConnected({ agentIcon: avatar, agentName: agent }),
              );
              dispatch(
                addWidgetComponents([
                  {
                    component_fields: {
                      agentIcon: avatar,
                      agentName: agent,
                      articles: articles,
                      attachments: attachments,
                      author: agent,
                      message: message,
                    },
                    component_type: 'text_message',
                    hideTimestamp: true,
                    isInAgentConvo: true,
                    isSent: true,
                  },
                ]),
              );
              break;
          }

          notificationBell.play().catch(() => {
            // noop: playback can fail if blocked by browser permissions
          });
        },
      );

      channel.bind(LIVE_CHAT_CONVERSATION_RESOLVED, () => {
        dispatch(concludeLiveChat());
      });
    };

    const unbindEvents = () => {
      channel.unbind(LIVE_CHAT_AGENT_MESSAGE);
      channel.unbind(LIVE_CHAT_CONVERSATION_RESOLVED);
    };

    bindEvents();

    return () => {
      unbindEvents();
    };
  }, [apiKey, conversationId, dispatch, isLiveChatMode, liveChatIntegration]);
};
